<template>
  <div class="pc-chat">
    <!-- 悬浮入口 -->
    <button type="button" class="pc-chat__fab" :class="{ 'is-on': open }" :title="$t('在线咨询')" @click="toggle">
      <v-icon color="white" size="24">{{ open ? 'mdi-close' : 'mdi-headset' }}</v-icon>
    </button>

    <!-- 聊天窗 -->
    <transition name="pc-fade">
      <div v-if="open" class="pc-chat__panel">
        <div class="pc-chat__hd" @mousedown="startDrag">
          <span class="pc-chat__av"><v-icon color="white" size="18">mdi-robot-happy-outline</v-icon></span>
          <div class="pc-chat__who">
            <b>{{ botName }}</b>
            <em>{{ onlineText }}</em>
          </div>
        </div>

        <div ref="box" class="pc-chat__bd">
          <!-- 接口没接上（后端未部署/网关异常）：不要把「未登录」这类内部提示甩给访客 -->
          <div v-if="offline" class="pc-chat__hello">
            <p>{{ $t('在线客服暂时没接上，稍后再试一次；着急的话直接发邮件，我们会尽快回。') }}</p>
            <a class="pc-chat__mail" href="mailto:liutaotao@taocarts.com">liutaotao@taocarts.com</a>
            <button type="button" class="pc-chat__retry" @click="retry">{{ $t('重新连接') }}</button>
          </div>

          <div v-else-if="!messages.length" class="pc-chat__hello">
            <p>{{ $t(welcome) }}</p>
            <div class="pc-chat__quick">
              <button v-for="q in quick" :key="q" type="button" class="pc-chat__q" @click="send(q)">{{ $t(q) }}</button>
            </div>
          </div>

          <div v-for="m in messages" :key="m.id" class="pc-msg" :class="'is-' + m.role">
            <div class="pc-msg__b">
              <div class="pc-msg__t">{{ m.content }}</div>

              <!-- 引导开通：套餐卡 -->
              <div v-if="m.pkgs && m.pkgs.length" class="pc-pkgs">
                <div v-for="p in m.pkgs" :key="p.package_key" class="pc-pkg" :class="{ 'is-trial': p.is_trial }">
                  <div class="pc-pkg__n">{{ $t(p.name) }}</div>
                  <div class="pc-pkg__p">
                    <template v-if="p.is_trial">{{ $t('免费') }} · {{ p.trial_days }} {{ $t('天') }}</template>
                    <template v-else>${{ p.price.month }} / {{ $t('月') }}</template>
                  </div>
                  <button type="button" class="pc-pkg__btn" @click="goSignUp(p)">{{ $t('免费开店') }}</button>
                </div>
              </div>

              <div class="pc-msg__m">
                <span v-if="m.role === 'ai'" class="pc-msg__src">{{ srcText(m.source) }}</span>
                <span>{{ m.time_text }}</span>
              </div>
            </div>
          </div>

          <div v-if="thinking" class="pc-msg is-ai">
            <div class="pc-msg__b pc-msg__b--typing">
              <i></i><i></i><i></i>
            </div>
          </div>
        </div>

        <div v-if="messages.length && quick.length" class="pc-chat__quick pc-chat__quick--ft">
          <button v-for="q in quick" :key="q" type="button" class="pc-chat__q" @click="send(q)">{{ $t(q) }}</button>
        </div>

        <div class="pc-chat__ft">
          <input v-model="text" class="pc-chat__inp" :placeholder="$t('想了解开店、选品、物流？直接问…')" @keyup.enter="send()" />
          <button type="button" class="pc-chat__send" :disabled="!text.trim() || thinking" @click="send()">
            <v-icon color="white" size="17">mdi-send</v-icon>
          </button>
        </div>

        <!-- 右下角拖拽手柄：拉宽拉高 -->
        <span class="pc-chat__rs" @mousedown="startResize" :title="$t('拖动调整大小')"></span>
      </div>
    </transition>
  </div>
</template>

<script>
import { chatBus } from '@/utils/chat'

/**
 * 官网「售前咨询」气泡：访客（想开店的潜在客户）问平台。
 *
 * 与店主后台的 SupportChat 同一个后端（Admin/Aisupport），
 * 但走访客接口（免登录，会话 source=visitor，凭证 session_sn）：
 *   - 售前人设（引导注册 / 试用），话术按「访客还没有账号」口径生成
 *   - 知识库命中不直答，交给大模型按访客视角改写
 *   - 套餐卡跳官网注册页（/sign-up）而不是后台订阅页
 * 后端有 IP 限流（每小时 20 问），前端只管展示。
 */
const SN_KEY = 'taocarts_presale_sn'

export default {
  name: 'PresaleChat',
  data () {
    return {
      open: false,
      loaded: false,
      thinking: false,
      offline: false,
      text: '',
      botName: '',
      welcome: '',
      quick: [],
      onlineText: '',
      sessionSn: '',
      messages: []
    }
  },
  mounted () {
    // 刷新后恢复上一次的会话（24 小时内后端才认同一个 sn 开的新会话记录）
    this.sessionSn = localStorage.getItem(SN_KEY) || ''
    // 页面其他位置（帮助中心搜索没命中、定价页问套餐…）通过总线把问题递进来
    chatBus.$on('open-chat', this.openWith)
  },
  beforeDestroy () {
    chatBus.$off('open-chat', this.openWith)
  },
  methods: {
    /**
     * 外部唤起：打开窗口，并把带过来的问题直接发出去
     * 首次唤起要先拿会话，等会话就绪再发，否则提问会落到一个不存在的 sn 上
     */
    openWith (question) {
      if (!this.open) {
        this.open = true
        this.$nextTick(this.scroll)
      }
      const q = String(question || '').trim()
      if (!this.loaded) {
        this.boot().then(() => {
          if (q) this.send(q)
        })
        return
      }
      if (this.offline) {
        this.retry().then(() => {
          if (q && !this.offline) this.send(q)
        })
        return
      }
      if (q) this.send(q)
    },

    retry () {
      this.offline = false
      this.loaded = false
      this.messages = []
      return this.boot()
    },

    /** 右下角手柄：拉宽拉高（最小 360×440，最大到视口边） */
    startResize (e) {
      if (e.button !== 0) return
      const panel = this.$el && this.$el.querySelector('.pc-chat__panel')
      if (!panel) return
      const r = panel.getBoundingClientRect()
      const sx = e.clientX
      const sy = e.clientY
      const sw = r.width
      const sh = r.height
      const onMove = (ev) => {
        const w = Math.max(360, Math.min(sw + ev.clientX - sx, window.innerWidth - 24))
        const h = Math.max(440, Math.min(sh + ev.clientY - sy, window.innerHeight - 24))
        panel.style.width = w + 'px'
        panel.style.height = h + 'px'
        panel.style.maxHeight = 'none'
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      e.preventDefault()
      e.stopPropagation()
    },

    /** 按住标题栏把聊天窗拖到任意位置（松手即停，保持在视口内） */
    startDrag (e) {
      if (e.button !== 0) return
      const panel = this.$el && this.$el.querySelector('.pc-chat__panel')
      if (!panel) return
      const r = panel.getBoundingClientRect()
      panel.style.insetInlineEnd = 'auto'
      panel.style.bottom = 'auto'
      panel.style.left = r.left + 'px'
      panel.style.top = r.top + 'px'
      const ox = e.clientX - r.left
      const oy = e.clientY - r.top
      const onMove = (ev) => {
        const x = Math.max(8, Math.min(ev.clientX - ox, window.innerWidth - r.width - 8))
        const y = Math.max(8, Math.min(ev.clientY - oy, window.innerHeight - 60))
        panel.style.left = x + 'px'
        panel.style.top = y + 'px'
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      e.preventDefault()
    },

    toggle () {
      this.open = !this.open
      if (this.open) {
        if (!this.loaded) this.boot()
        this.$nextTick(this.scroll)
      }
    },

    /** 开场：客服名 / 欢迎语 / 快捷问题（并恢复历史消息） */
    boot () {
      return this.$api.aiSupport.visitorBootstrap({}).then(res => {
        // status 不是 1（后端没部署访客接口时会回 -4 未登录）不能当正常空态：
        // 否则访客打开就是一个空白框，什么都问不了
        if (!res || Number(res.status) !== 1) {
          this.offline = true
          this.loaded = true
          return
        }
        const d = res.data || {}
        this.offline = false
        this.botName = d.bot_name || this.$t('开店顾问')
        this.welcome = d.welcome || ''
        this.quick = d.quick_questions || []
        this.onlineText = d.work_time ? (this.$t('人工在线') + ' ' + d.work_time) : this.$t('全天候自动应答')
        // 后端每次 bootstrap 都开新会话；有旧 sn 就先拉旧记录，避免刷新丢上下文
        if (this.sessionSn) {
          this.restore()
        } else if (d.session_sn) {
          this.sessionSn = d.session_sn
          localStorage.setItem(SN_KEY, d.session_sn)
        }
        this.loaded = true
        this.$nextTick(this.scroll)
      }).catch(() => {
        this.offline = true
        this.loaded = true
      })
    },

    /** 恢复历史消息（sn 失效则静默重开） */
    restore () {
      return this.$api.aiSupport.visitorMessages({ session_sn: this.sessionSn }).then(res => {
        // 已经通过外部唤起发过消息了就别用历史覆盖，否则刚问的那句会消失
        if (!this.messages.length && res && Number(res.status) === 1 && res.data && Array.isArray(res.data.records)) {
          this.messages = res.data.records
        }
        this.$nextTick(this.scroll)
      }).catch(() => {})
    },

    send (q) {
      const content = q || this.text
      if (!content || !content.trim() || this.thinking) return
      this.text = ''
      this.messages.push({
        id: 'tmp' + Date.now(),
        role: 'user',
        content: content,
        time_text: this.now()
      })
      this.thinking = true
      this.$nextTick(this.scroll)

      this.$api.aiSupport.visitorAsk({
        session_sn: this.sessionSn,
        question: content,
        lang: this.$store.state.langdef || ''
      }).then(res => {
        this.thinking = false
        if (!res || Number(res.status) !== 1) {
          // -4 是「未登录」，属于后端没部署访客接口的内部状态，别直白甩给访客
          if (res && Number(res.status) === -4) {
            this.offline = true
            return
          }
          this.pushAi((res && res.msg) || this.$t('网络异常，请稍后再试'), 'fallback')
          return
        }
        const d = res.data || {}
        if (d.session_sn && d.session_sn !== this.sessionSn) {
          this.sessionSn = d.session_sn
          localStorage.setItem(SN_KEY, d.session_sn)
        }
        this.pushAi(d.reply, d.source, d.packages)
      }).catch(() => {
        this.thinking = false
        this.pushAi(this.$t('网络异常，请稍后再试'), 'fallback')
      })
    },

    pushAi (content, source, pkgs) {
      this.messages.push({
        id: 'ai' + Date.now(),
        role: 'ai',
        content: content,
        source: source,
        pkgs: pkgs || [],
        time_text: this.now()
      })
      this.$nextTick(this.scroll)
    },

    goSignUp (p) {
      this.open = false
      this.$router.push({ path: '/sign-up' })
    },

    srcText (s) {
      const map = {
        llm: this.$t('AI 顾问'),
        fallback: this.$t('待人工确认'),
        rule: this.$t('转人工')
      }
      return map[s] || this.$t('AI 顾问')
    },

    now () {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : n)
      return p(d.getHours()) + ':' + p(d.getMinutes())
    },

    scroll () {
      const box = this.$refs.box
      if (box) box.scrollTop = box.scrollHeight
    }
  }
}
</script>

<style scoped>
/* 与官网品牌色一致（--p 在 website.scss 定义），此处固化一份防加载顺序问题 */
.pc-chat__fab {
  position: fixed;
  bottom: 26px;
  inset-inline-end: 22px;
  z-index: 9999;
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #8a4dff 0%, #7331FF 100%);
  box-shadow: 0 6px 18px rgba(115, 49, 255, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pc-chat__fab.is-on {
  background: #8b8b9a;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
}

.pc-chat__panel {
  position: fixed;
  bottom: 92px;
  inset-inline-end: 22px;
  z-index: 9999;
  width: 448px;
  max-width: calc(100vw - 40px);
  height: 620px;
  max-height: calc(100vh - 130px);
  background: #fff;
  border: 1px solid #e6e6ef;
  border-radius: 14px;
  box-shadow: 0 12px 34px rgba(24, 20, 60, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pc-chat__hd {
  cursor: move;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #8a4dff 0%, #7331FF 100%);
  color: #fff;
  flex: none;
}

.pc-chat__av {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.pc-chat__who { display: flex; flex-direction: column; min-width: 0; }
.pc-chat__who b { font-size: 13.2px; line-height: 1.3; }
.pc-chat__who em { font-style: normal; font-size: 11px; opacity: .85; }

.pc-chat__bd {
  flex: 1;
  overflow-y: auto;
  padding: 13px 13px 6px;
  background: #fafafe;
}

.pc-chat__hello {
  font-size: 12.7px;
  line-height: 1.8;
  color: #4b4b5a;
  background: #fff;
  border: 1px solid #ececf6;
  border-radius: 11px;
  padding: 11px 12px;
}

.pc-chat__hello p { margin: 0 0 9px; }

/* 接口没接上时的兜底：给一条真能用得上的路，而不是空白 */
.pc-chat__mail {
  display: block;
  margin-bottom: 9px;
  color: #7331FF;
  font-size: 12.4px;
  word-break: break-all;
}

.pc-chat__retry {
  border: 1px solid #ddd9fb;
  background: #f4f2ff;
  color: #7331FF;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
}

.pc-chat__retry:hover { background: #e9e5ff; }

.pc-chat__quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pc-chat__quick--ft {
  padding: 8px 13px;
  background: #fff;
  border-top: 1px solid #f0f0f6;
  flex: none;
}

.pc-chat__q {
  border: 1px solid #ddd9fb;
  background: #f4f2ff;
  color: #7331FF;
  border-radius: 99px;
  padding: 4px 10px;
  font-size: 11.6px;
  cursor: pointer;
  max-width: 100%;
  text-align: left;
}

.pc-chat__q:hover { background: #e9e5ff; }

.pc-msg { display: flex; margin-bottom: 11px; }
.pc-msg.is-user { justify-content: flex-end; }

.pc-msg__b {
  max-width: 84%;
  border-radius: 12px;
  padding: 9px 12px;
  font-size: 12.7px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}

.pc-msg.is-user .pc-msg__b {
  background: #7331FF;
  color: #fff;
  border-top-right-radius: 3px;
}

.pc-msg.is-ai .pc-msg__b {
  background: #fff;
  border: 1px solid #ececf6;
  color: #2f2f3d;
  border-top-left-radius: 3px;
}

.pc-msg__b--typing {
  display: inline-flex;
  gap: 4px;
  padding: 12px 14px;
}

.pc-msg__b--typing i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9c6dd;
  animation: pc-blink 1.1s infinite;
}

.pc-msg__b--typing i:nth-child(2) { animation-delay: .18s; }
.pc-msg__b--typing i:nth-child(3) { animation-delay: .36s; }

@keyframes pc-blink {
  0%, 100% { opacity: .35; }
  50% { opacity: 1; }
}

.pc-msg__m {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 6px;
  font-size: 10.8px;
  color: #9393a5;
}

.pc-msg__src {
  border: 1px solid #ececf6;
  border-radius: 5px;
  padding: 0 5px;
  background: #fafafe;
}

/* 套餐卡 */
.pc-pkgs {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 9px;
}

.pc-pkg {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  border: 1px solid #ececf6;
  border-radius: 9px;
  padding: 8px 10px;
  background: #fbfbff;
}

.pc-pkg__n {
  flex: 1 0 100%;
  font-size: 12.4px;
  font-weight: 700;
  color: #2f2f3d;
  min-width: 0;
  overflow-wrap: break-word;
}

.pc-pkg__p {
  flex: 1 1 auto;
  font-size: 11.6px;
  color: #6b6b80;
}

.pc-pkg.is-trial {
  border-color: #bfe6d3;
  background: #f2fbf6;
}

.pc-pkg__btn {
  border: 0;
  background: #7331FF;
  color: #fff;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 11.8px;
  cursor: pointer;
  flex: none;
}

.pc-pkg__btn:hover { background: #8a4dff; }

/* 右下角拖拽手柄（拉宽拉高） */
.pc-chat__rs {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 50%, rgba(0, 0, 0, 0.18) 50%);
  border-bottom-right-radius: 14px;
  z-index: 2;
}

.pc-chat__ft {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-top: 1px solid #f0f0f6;
  flex: none;
}

.pc-chat__inp {
  flex: 1;
  border: 1px solid #e3e3ef;
  border-radius: 9px;
  padding: 8px 11px;
  font-size: 12.8px;
  outline: none;
  min-width: 0;
}

.pc-chat__inp:focus { border-color: #7331FF; }

.pc-chat__send {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 9px;
  background: #7331FF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: none;
}

.pc-chat__send:disabled { background: #b9b9c9; cursor: not-allowed; }

.pc-fade-enter-active { transition: opacity .18s, transform .18s; }
.pc-fade-enter { opacity: 0; transform: translateY(8px); }

@media (max-width: 600px) {
  .pc-chat__panel {
    inset-inline-end: 12px;
    bottom: 84px;
    width: calc(100vw - 24px);
  }
  .pc-chat__fab {
    inset-inline-end: 14px;
    bottom: 20px;
  }
}
</style>
